// tabs/SendersTab.jsx
import React, { useEffect, useState } from "react";
import {
  FaCheckCircle,
  FaEdit,
  FaEnvelope,
  FaPlus,
  FaPowerOff,
  FaStar,
  FaSync,
} from "react-icons/fa";

import {
  deleteEmailSender,
  getEmailSenders,
  setDefaultEmailSender,
} from "../../../../../services/mailingServices";

export default function SendersTab({
  refreshKey = 0,
  onCreateSender,
  onEditSender,
}) {
  const [loading, setLoading] = useState(false);
  const [senders, setSenders] = useState([]);

  const fetchSenders = async () => {
    try {
      setLoading(true);
      const rows = await getEmailSenders();
      setSenders(rows || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSenders();
    // eslint-disable-next-line
  }, [refreshKey]);

  const onSetDefault = async (id) => {
    await setDefaultEmailSender(id);
    fetchSenders();
  };

  const onDisable = async (sender) => {
    if (
      !window.confirm(
        `Désactiver l'expéditeur "${sender.label}" ? Il ne sera plus utilisé dans les nouvelles campagnes.`
      )
    ) {
      return;
    }

    await deleteEmailSender(sender.id);
    fetchSenders();
  };

  const activeSenders = senders.filter(
    (sender) => sender.is_active !== false && sender.is_active !== 0
  );

  const inactiveSenders = senders.filter(
    (sender) => sender.is_active === false || sender.is_active === 0
  );

  return (
    <div className="space-y-5">
      <div className="rounded-3xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-sm p-5">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold">Comptes expéditeurs</h2>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
              Gérez les identités d’envoi utilisées par vos campagnes.
            </p>
          </div>

          <div className="flex gap-2">
            <button
              type="button"
              onClick={fetchSenders}
              className="inline-flex items-center gap-2 rounded-2xl bg-slate-100 dark:bg-slate-800 px-3 py-2 text-sm"
            >
              <FaSync /> Actualiser
            </button>

            <button
              type="button"
              onClick={onCreateSender}
              className="inline-flex items-center gap-2 rounded-2xl bg-slate-950 text-white dark:bg-slate-100 dark:text-slate-950 px-3 py-2 text-sm"
            >
              <FaPlus /> Nouvel expéditeur
            </button>
          </div>
        </div>

        <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-3">
          <SenderMetric
            label="Total"
            value={senders.length}
            helper="tous comptes"
          />
          <SenderMetric
            label="Actifs"
            value={activeSenders.length}
            helper="utilisables"
          />
          <SenderMetric
            label="Inactifs"
            value={inactiveSenders.length}
            helper="désactivés"
          />
        </div>
      </div>

      <div className="rounded-3xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-sm p-5">
        <div className="font-semibold">Expéditeurs actifs</div>

        <div className="mt-4 grid gap-3">
          {loading ? (
            <div className="text-sm text-slate-500">Chargement…</div>
          ) : activeSenders.length === 0 ? (
            <EmptySenders onCreate={onCreateSender} />
          ) : (
            activeSenders.map((sender) => (
              <SenderCard
                key={sender.id}
                sender={sender}
                onEdit={() => onEditSender(sender)}
                onSetDefault={() => onSetDefault(sender.id)}
                onDisable={() => onDisable(sender)}
              />
            ))
          )}
        </div>
      </div>

      {inactiveSenders.length > 0 && (
        <div className="rounded-3xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-sm p-5">
          <div className="font-semibold">Expéditeurs désactivés</div>

          <div className="mt-4 grid gap-3 opacity-75">
            {inactiveSenders.map((sender) => (
              <SenderCard
                key={sender.id}
                sender={sender}
                onEdit={() => onEditSender(sender)}
                onSetDefault={() => onSetDefault(sender.id)}
                onDisable={() => onDisable(sender)}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function SenderMetric({ label, value, helper }) {
  return (
    <div className="rounded-3xl bg-slate-50 dark:bg-slate-800/70 p-4 border border-slate-100 dark:border-slate-800">
      <div className="text-xs text-slate-500 dark:text-slate-400">
        {label}
      </div>
      <div className="mt-1 text-3xl font-bold tabular-nums">{value}</div>
      <div className="mt-1 text-xs text-slate-500">{helper}</div>
    </div>
  );
}

function SenderCard({ sender, onEdit, onSetDefault, onDisable }) {
  const isDefault = sender.is_default === true || sender.is_default === 1;
  const isActive = sender.is_active !== false && sender.is_active !== 0;

  return (
    <div
      className={[
        "rounded-3xl border p-4 transition",
        isDefault
          ? "border-emerald-200 bg-emerald-50/60 dark:border-emerald-900/60 dark:bg-emerald-950/20"
          : "border-slate-200 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/40",
      ].join(" ")}
    >
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <div className="inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-200">
              <FaEnvelope />
            </div>

            <div>
              <h3 className="font-semibold">{sender.label}</h3>

              <div className="text-sm text-slate-600 dark:text-slate-300">
                {sender.from_name} &lt;{sender.from_email}&gt;
              </div>
            </div>

            {isDefault && (
              <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-200 px-2.5 py-1 text-xs">
                <FaCheckCircle />
                Défaut
              </span>
            )}

            {!isActive && (
              <span className="rounded-full bg-rose-100 text-rose-700 dark:bg-rose-950/40 dark:text-rose-200 px-2.5 py-1 text-xs">
                Inactif
              </span>
            )}
          </div>

          <div className="mt-4 grid grid-cols-1 md:grid-cols-4 gap-2 text-xs">
            <InfoPill label="Reply-to" value={sender.reply_to || "—"} />
            <InfoPill
              label="SMTP"
              value={`${sender.smtp_host || "—"}:${sender.smtp_port || "—"}`}
            />
            <InfoPill label="Limite jour" value={sender.daily_limit || 0} />
            <InfoPill label="Limite heure" value={sender.hourly_limit || 0} />
          </div>
        </div>

        <div className="flex flex-wrap gap-2 shrink-0">
          <button
            type="button"
            onClick={onEdit}
            className="inline-flex items-center gap-2 rounded-xl bg-slate-100 dark:bg-slate-800 px-3 py-2 text-sm"
          >
            <FaEdit /> Éditer
          </button>

          <button
            type="button"
            disabled={isDefault || !isActive}
            onClick={onSetDefault}
            className="inline-flex items-center gap-2 rounded-xl bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-200 px-3 py-2 text-sm disabled:opacity-50"
          >
            <FaStar /> Par défaut
          </button>

          {isActive && (
            <button
              type="button"
              onClick={onDisable}
              className="inline-flex items-center gap-2 rounded-xl bg-rose-50 text-rose-700 dark:bg-rose-950/40 dark:text-rose-200 px-3 py-2 text-sm"
            >
              <FaPowerOff /> Désactiver
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function InfoPill({ label, value }) {
  return (
    <div className="rounded-2xl bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 px-3 py-2 min-w-0">
      <div className="text-slate-400">{label}</div>
      <div className="font-medium truncate">{value}</div>
    </div>
  );
}

function EmptySenders({ onCreate }) {
  return (
    <div className="rounded-3xl border border-dashed border-slate-300 dark:border-slate-700 p-8 text-center">
      <FaEnvelope className="mx-auto text-3xl text-slate-400" />
      <div className="mt-3 font-semibold">Aucun expéditeur actif</div>
      <p className="mt-1 text-sm text-slate-500">
        Ajoutez au moins une adresse d’envoi avant de créer une campagne.
      </p>

      <button
        type="button"
        onClick={onCreate}
        className="mt-4 inline-flex items-center gap-2 rounded-2xl bg-slate-950 text-white dark:bg-slate-100 dark:text-slate-950 px-4 py-2 text-sm"
      >
        <FaPlus /> Créer un expéditeur
      </button>
    </div>
  );
}